// src/components/notifications/ToastNotificationProvider.tsx
import React, { ReactNode, useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import { 
  IconBellRinging, 
  IconCheck, 
  IconAlertTriangle, 
  IconInfoCircle,
  IconX,
  IconClock,
  IconUserPlus,
  IconChecks,
  IconExclamationMark
} from '@tabler/icons-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { Notification, NotificationType, NotificationPriority } from '../../types/notifications';
import { Link } from '@tanstack/react-router';
import { Button, Group, Text } from '@mantine/core';

interface ToastNotificationProviderProps {
  children: ReactNode;
}

export function ToastNotificationProvider({ children }: ToastNotificationProviderProps) {
  const { notifications: allNotifications, settings, markAsRead } = useNotifications();
  
  useEffect(() => {
    // Find new unread notifications that should be shown as toasts
    const newNotifications = allNotifications
      .filter(n => !n.isRead && settings.enabledTypes.includes(n.type))
      .slice(0, settings.maxToasts);

    // Show toast for each new notification
    newNotifications.forEach(notification => {
      if (settings.enableToasts) {
        showToastNotification(notification, markAsRead, settings.autoHideDelay);
      }
    });
  }, [allNotifications, settings, markAsRead]);

  return <>{children}</>;
}

function showToastNotification(
  notification: Notification, 
  markAsRead: (id: string) => void,
  autoHideDelay: number
) {
  const { icon, color } = getNotificationStyle(notification);
  
  const toastId = notifications.show({
    id: notification.id,
    title: notification.title,
    message: (
      <NotificationToastContent 
        notification={notification} 
        onMarkAsRead={() => {
          markAsRead(notification.id);
          notifications.hide(notification.id);
        }}
      />
    ),
    icon,
    color,
    autoClose: notification.priority === NotificationPriority.Critical ? false : autoHideDelay,
    withCloseButton: true,
    position: 'top-right',
    style: { maxWidth: 400 },
  });

  return toastId;
}

interface NotificationToastContentProps {
  notification: Notification;
  onMarkAsRead: () => void;
}

function NotificationToastContent({ notification, onMarkAsRead }: NotificationToastContentProps) {
  return (
    <div>
      <Text size="sm" mb={notification.actionUrl ? "xs" : 0}>
        {notification.message}
      </Text>
      
      {notification.actionUrl && (
        <Group gap="xs" mt="xs">
          <Button 
            size="xs" 
            variant="light" 
            component={Link} 
            to={notification.actionUrl}
            onClick={onMarkAsRead}
          >
            {notification.actionText || 'Visualizza'}
          </Button>
          <Button 
            size="xs" 
            variant="subtle" 
            onClick={onMarkAsRead}
          >
            Segna come letto
          </Button>
        </Group>
      )}
    </div>
  );
}

function getNotificationStyle(notification: Notification): { icon: React.ReactNode; color: string } {
  switch (notification.type) {
    case NotificationType.Assignment:
      return { 
        icon: <IconUserPlus size="1.1rem" />, 
        color: 'blue' 
      };
    
    case NotificationType.TaskCompleted:
      return { 
        icon: <IconChecks size="1.1rem" />, 
        color: 'green' 
      };
    
    case NotificationType.DeadlineAlert:
      return { 
        icon: <IconClock size="1.1rem" />, 
        color: notification.priority === NotificationPriority.Critical ? 'red' : 'orange' 
      };
    
    case NotificationType.StatusUpdate:
      return { 
        icon: <IconInfoCircle size="1.1rem" />, 
        color: 'cyan' 
      };
    
    case NotificationType.Success:
      return { 
        icon: <IconCheck size="1.1rem" />, 
        color: 'green' 
      };
    
    case NotificationType.Warning:
      return { 
        icon: <IconAlertTriangle size="1.1rem" />, 
        color: 'yellow' 
      };
    
    case NotificationType.Error:
      return { 
        icon: <IconX size="1.1rem" />, 
        color: 'red' 
      };
    
    case NotificationType.Info:
      return { 
        icon: <IconInfoCircle size="1.1rem" />, 
        color: 'blue' 
      };
    
    case NotificationType.System:
    default:
      return { 
        icon: <IconBellRinging size="1.1rem" />, 
        color: 'gray' 
      };
  }
}